import { ReactComponent as DocumentIcon } from "@assets/icons/document.svg";
import { ReactComponent as DownloadIcon } from "@assets/icons/download.svg";
import { ReactComponent as PennyIcon } from "@assets/icons/penny_icon.svg";
import BottomBar from "@components/BottomBars/BottomBar";
import BottomBarDesktop from "@components/BottomBars/BottomBarDesktop";
import HeaderProgress from "@components/Progress/HeaderProgress";
import { auth, storage } from "@utils/firebase";
import { useGetUser } from "@utils/hooks";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import React, { useMemo, useState, useEffect } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useParams } from "react-router-dom";

type RecommendationsDetailsProps = {};

type FileType = "sustenability" | "preliminary" | "contract";

interface FileAvailability {
  sustenability: boolean;
  preliminary: boolean;
  contract: boolean;
}

const RecommendationsDetails: React.FC<RecommendationsDetailsProps> = () => {
  const { zurich_details } = useParams();
  const [user] = useAuthState(auth);
  const { data: userData, isLoading: userDataLoading } = useGetUser();
  
  const [fileAvailability, setFileAvailability] = useState<FileAvailability>({
    sustenability: false,
    preliminary: false,
    contract: false,
  });
  const [filesChecked, setFilesChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [downloadingFile, setDownloadingFile] = useState<FileType | null>(null);

  const variantsZurichDetails = {
    zurich_standard: {
      title: "Zurich Standard PRSA details and overview",
    },
    zurich_nonstandard: {
      title: "Zurich Non-Standard PRSA details and overview",
    },
  };

  // Check file availability on mount
  useEffect(() => {
    const checkFileAvailability = async () => {
      if (!user?.uid || !zurich_details) return;

      const files = {
        sustenability: "Sustainability_Statement.pdf",
        preliminary: "Preliminary_Disclosure_Certificate.pdf",
        contract: "Contract_Document.pdf",
      };

      const availability: FileAvailability = {
        sustenability: false,
        preliminary: false,
        contract: false,
      };

      for (const [key, fileName] of Object.entries(files)) {
        try {
          const fileRef = storageRef(
            storage,
            `${user.uid}/${zurich_details}/${fileName}`
          );
          await getDownloadURL(fileRef);
          availability[key as FileType] = true;
        } catch (error) {
          // File doesn't exist or can't be accessed
          availability[key as FileType] = false;
        }
      }

      setFileAvailability(availability);
      setFilesChecked(true);
    };

    checkFileAvailability();
  }, [user?.uid, zurich_details]);

  const onDownloadFile = async (type: FileType) => {
    if (!fileAvailability[type]) {
      setErrorMessage(`The ${type} document is not available for download.`);
      setTimeout(() => setErrorMessage(""), 5000);
      return;
    }

    setDownloadingFile(type);
    setErrorMessage("");

    const files = {
      sustenability: "Sustainability_Statement.pdf",
      preliminary: "Preliminary_Disclosure_Certificate.pdf",
      contract: "Contract_Document.pdf",
    };

    try {
      const storageRefChild = storageRef(
        storage,
        `${user?.uid}/${zurich_details}/${files[type]}`
      );

      const url = await getDownloadURL(storageRefChild);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "true");

      document.body.append(link);
      setTimeout(() => {
        link.click();
        link.parentNode?.removeChild(link);
      });
    } catch (error) {
      console.error("Download error:", error);
      setErrorMessage("Failed to download the document. Please try again or contact support.");
      setTimeout(() => setErrorMessage(""), 5000);
    } finally {
      setDownloadingFile(null);
    }
  };

  const userDataPension = useMemo(() => {
    if (!userDataLoading) {
      return {
        often_contribution:
          userData?.often_contribution && userData?.often_contribution?.length
            ? userData?.often_contribution
            : "Not available",
        value_contribution: userData?.pension_contribution_value
          ? new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(userData?.pension_contribution_value) + " per month"
          : "Not available",
        time_contribution:
          userData?.time_contribution && userData?.time_contribution.length
            ? userData?.time_contribution + " years"
            : "Not available",
      };
    }
  }, [userData, userDataLoading]);

  return (
    <section className="grow flex items-start md:items-center justify-center border-t-4 border-l-0 md:border-t-0 md:border-l-4 border-[#F4E34E] px-0 md:px-2 lg:px-0">
      <div className="max-w-full md:max-w-[846px] w-full md:rounded-3xl md:border md:border-pn_gray-200">
        <HeaderProgress hideSteps />

        <div className="flex flex-col gap-y-2 md:gap-y-0 md:flex-row md:items-stretch">
          <div className="pl-4 pr-4 pt-6 pb-4 md:pl-8 md:pb-6 md:pr-6 flex-1">
            <div className="flex flex-col gap-y-6 p-0 md:p-4">
              <div className="bg-[#F4E34E] rounded-full w-[60px] h-[60px] flex items-center justify-center">
                <PennyIcon className="w-6 h-[27px]" />
              </div>
              <div className="flex flex-col gap-y-2">
                <span className="text-pn_gray-950 text-pn-large-title">
                  {
                    variantsZurichDetails[
                      zurich_details as keyof typeof variantsZurichDetails
                    ].title
                  }
                </span>
                <div className="flex flex-col pt-2 gap-y-4">
                  <span className="text-pn-body-emphasis">
                    Recommendation Summary
                  </span>
                  <div className="flex flex-col gap-y-4 text-pn-body-regular">
                    <span>
                      You are enrolling in a standard PRSA pension with the
                      intention to:
                    </span>
                    <div className="flex flex-col">
                      <span>
                        1. How often you're contributing:{" "}
                        {userDataPension?.often_contribution}
                      </span>
                      <span>
                        2. How much you're contributing:{" "}
                        {userDataPension?.value_contribution}
                      </span>
                      <span>
                        3. For How long: {userDataPension?.time_contribution}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="py-4 md:py-6 pl-4 pr-4 md:pl-6 md:pr-8 flex flex-col gap-y-2 flex-1">
            <div className="flex flex-col gap-y-4">
              <span className="text-pn-body-emphasis">
                Assessment Documents
              </span>
              <span className="text-pn-body-regular">
                Download and read all the documents to get a full understanding
                on what this plan means for you.
              </span>
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <span className="text-red-700 text-sm">{errorMessage}</span>
                </div>
              )}
            </div>

            <div className="flex pt-5 flex-col gap-y-2">
              <button
                onClick={() => onDownloadFile("sustenability")}
                disabled={!filesChecked || !fileAvailability.sustenability || downloadingFile === "sustenability"}
                className={`flex items-center gap-x-3 border rounded-2xl p-4 transition-all ${
                  !filesChecked || !fileAvailability.sustenability
                    ? "border-pn_gray-100 bg-pn_gray-50 cursor-not-allowed opacity-60"
                    : downloadingFile === "sustenability"
                    ? "border-pn_gray-200 bg-pn_gray-50 cursor-wait"
                    : "border-pn_gray-200 bg-pn_white hover:bg-pn_gray-25 cursor-pointer"
                }`}
              >
                <div className={`rounded-full w-10 h-10 flex items-center justify-center ${
                  !filesChecked || !fileAvailability.sustenability ? "bg-pn_gray-100" : "bg-pn_paper-3"
                }`}>
                  <DocumentIcon className={!filesChecked || !fileAvailability.sustenability ? "opacity-50" : ""} />
                </div>
                <div className="flex items-center flex-1">
                  <div className="flex flex-col flex-1 text-left">
                    <span className={`text-pn-body-regular ${
                      !filesChecked || !fileAvailability.sustenability ? "text-pn_gray-400" : ""
                    }`}>
                      Sustainability Statement
                    </span>
                    {!filesChecked && (
                      <span className="text-xs text-pn_gray-400">Checking availability...</span>
                    )}
                    {filesChecked && !fileAvailability.sustenability && (
                      <span className="text-xs text-red-500">File not available</span>
                    )}
                  </div>
                  <div className="p-2">
                    {downloadingFile === "sustenability" ? (
                      <div className="w-5 h-5 border-2 border-pn_gray-300 border-t-pn_gray-600 rounded-full animate-spin"></div>
                    ) : (
                      <DownloadIcon className={!filesChecked || !fileAvailability.sustenability ? "opacity-50" : ""} />
                    )}
                  </div>
                </div>
              </button>

              <button
                onClick={() => onDownloadFile("preliminary")}
                disabled={!filesChecked || !fileAvailability.preliminary || downloadingFile === "preliminary"}
                className={`flex items-center gap-x-3 border rounded-2xl p-4 transition-all ${
                  !filesChecked || !fileAvailability.preliminary
                    ? "border-pn_gray-100 bg-pn_gray-50 cursor-not-allowed opacity-60"
                    : downloadingFile === "preliminary"
                    ? "border-pn_gray-200 bg-pn_gray-50 cursor-wait"
                    : "border-pn_gray-200 bg-pn_white hover:bg-pn_gray-25 cursor-pointer"
                }`}
              >
                <div className={`rounded-full w-10 h-10 flex items-center justify-center ${
                  !filesChecked || !fileAvailability.preliminary ? "bg-pn_gray-100" : "bg-pn_paper-3"
                }`}>
                  <DocumentIcon className={!filesChecked || !fileAvailability.preliminary ? "opacity-50" : ""} />
                </div>
                <div className="flex items-center flex-1">
                  <div className="flex flex-col flex-1 text-left">
                    <span className={`text-pn-body-regular ${
                      !filesChecked || !fileAvailability.preliminary ? "text-pn_gray-400" : ""
                    }`}>
                      Preliminary Disclosure Certificate
                    </span>
                    {!filesChecked && (
                      <span className="text-xs text-pn_gray-400">Checking availability...</span>
                    )}
                    {filesChecked && !fileAvailability.preliminary && (
                      <span className="text-xs text-red-500">File not available</span>
                    )}
                  </div>
                  <div className="p-2">
                    {downloadingFile === "preliminary" ? (
                      <div className="w-5 h-5 border-2 border-pn_gray-300 border-t-pn_gray-600 rounded-full animate-spin"></div>
                    ) : (
                      <DownloadIcon className={!filesChecked || !fileAvailability.preliminary ? "opacity-50" : ""} />
                    )}
                  </div>
                </div>
              </button>

              <button
                onClick={() => onDownloadFile("contract")}
                disabled={!filesChecked || !fileAvailability.contract || downloadingFile === "contract"}
                className={`flex items-center gap-x-3 border rounded-2xl p-4 transition-all ${
                  !filesChecked || !fileAvailability.contract
                    ? "border-pn_gray-100 bg-pn_gray-50 cursor-not-allowed opacity-60"
                    : downloadingFile === "contract"
                    ? "border-pn_gray-200 bg-pn_gray-50 cursor-wait"
                    : "border-pn_gray-200 bg-pn_white hover:bg-pn_gray-25 cursor-pointer"
                }`}
              >
                <div className={`rounded-full w-10 h-10 flex items-center justify-center ${
                  !filesChecked || !fileAvailability.contract ? "bg-pn_gray-100" : "bg-pn_paper-3"
                }`}>
                  <DocumentIcon className={!filesChecked || !fileAvailability.contract ? "opacity-50" : ""} />
                </div>
                <div className="flex items-center flex-1">
                  <div className="flex flex-col flex-1 text-left">
                    <span className={`text-pn-body-regular ${
                      !filesChecked || !fileAvailability.contract ? "text-pn_gray-400" : ""
                    }`}>
                      Contract Document
                    </span>
                    {!filesChecked && (
                      <span className="text-xs text-pn_gray-400">Checking availability...</span>
                    )}
                    {filesChecked && !fileAvailability.contract && (
                      <span className="text-xs text-red-500">File not available</span>
                    )}
                  </div>
                  <div className="p-2">
                    {downloadingFile === "contract" ? (
                      <div className="w-5 h-5 border-2 border-pn_gray-300 border-t-pn_gray-600 rounded-full animate-spin"></div>
                    ) : (
                      <DownloadIcon className={!filesChecked || !fileAvailability.contract ? "opacity-50" : ""} />
                    )}
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>

        <div className="hidden md:block">
          <BottomBarDesktop
            labelBack="Back to Recommendations"
            onBack={"/"}
            insideCard
          />
        </div>

        <div className="block md:hidden pt-2 pb-10">
          <BottomBar
            bordered={false}
            layout="single"
            variantsPrimary="secondary"
            labelNext="Back to Recommendations"
            onNext={"/"}
          />
        </div>
      </div>
    </section>
  );
};
export default RecommendationsDetails;
